/**
 * Object pool.
 *
 * Missiles, particles, decoys and debris are created and destroyed constantly; allocating them
 * per-event in a 120 Hz loop is the most likely source of GC frame-time spikes in this project
 * (SPEC §4.3, §9). Everything transient is pooled.
 *
 * The high-water mark is the number that matters when tuning capacity — it is displayed in the
 * debug overlay so an under-sized pool shows up as growth rather than as a mystery hitch.
 */

/** The read-only face of a pool, for the debug overlay. */
export interface PoolStats {
  readonly name: string;
  readonly inUse: number;
  readonly free: number;
  readonly capacity: number;
  readonly highWaterMark: number;
}

/** Every pool registers itself so the debug overlay can report usage without being wired to each. */
const registeredPools: PoolStats[] = [];

/** All live pools, in creation order. Debug/reporting only. */
export function allPools(): readonly PoolStats[] {
  return registeredPools;
}

export class Pool<T extends object> implements PoolStats {
  readonly name: string;

  private readonly factory: () => T;
  private readonly reset: (item: T) => void;
  private readonly free_: T[] = [];

  private inUse_ = 0;
  private capacity_ = 0;
  private highWaterMark_ = 0;

  /**
   * @param name          Label shown in the debug overlay.
   * @param factory       Allocates one instance. Called at construction and on growth only.
   * @param reset         Returns an instance to its neutral state; called on release.
   * @param initialCapacity Instances pre-allocated up front. Size this from the high-water mark.
   */
  constructor(name: string, factory: () => T, reset: (item: T) => void, initialCapacity: number) {
    this.name = name;
    this.factory = factory;
    this.reset = reset;
    for (let i = 0; i < initialCapacity; i += 1) {
      this.free_.push(factory());
    }
    this.capacity_ = initialCapacity;
    registeredPools.push(this);
  }

  /**
   * Take an instance from the pool. Grows (allocating) if empty — growth during play is a
   * tuning bug, not a design feature, and shows up in the overlay's high-water mark.
   */
  acquire(): T {
    const item = this.free_.pop();
    this.inUse_ += 1;
    if (this.inUse_ > this.highWaterMark_) this.highWaterMark_ = this.inUse_;
    if (item !== undefined) return item;
    this.capacity_ += 1;
    return this.factory();
  }

  /**
   * Return an instance. Releasing an item twice, or one that never came from this pool, corrupts
   * the free list — the pool does not police it, because the bookkeeping would cost more than
   * the bug is worth. Release exactly once, from the owner.
   */
  release(item: T): void {
    this.reset(item);
    this.free_.push(item);
    this.inUse_ -= 1;
  }

  get inUse(): number {
    return this.inUse_;
  }

  get free(): number {
    return this.free_.length;
  }

  get capacity(): number {
    return this.capacity_;
  }

  get highWaterMark(): number {
    return this.highWaterMark_;
  }

  /** Forget the high-water mark — call at mission start so the reading is per-mission. */
  resetHighWaterMark(): void {
    this.highWaterMark_ = this.inUse_;
  }
}
